import Section from "@/pages/settings/components/sections/Section";
import AddActionButton from "@/components/ui/add-action-button";
import Text from "@/components/ui/text";
import ToastBody from "@/components/ui/toast-body";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { DealStage } from "@/pages/deals/types";
import Spinner from "@/components/ui/spinner";
import { DealSettings } from "@dreamhub/designsystem/icons";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import {
  useDealStages,
  useCreateDealStage,
  useUpdateDealStagesOrder
} from "@/entities/settings/deal/hooks";
import CreateNewStage, { newStageFormData } from "./CreateNewStage";
import DealStageRow from "./DealStageRow";
import DraggableStagesList from "@/pages/settings/components/shared/stageStudio/DraggableStagesList";
import { calculateNewStageOrder } from "@/pages/settings/components/shared/stageStudio/calculateNewStageOrder";

export type confirmationModalType = "moveStageForward" | "moveStageBackward";

const TRANSLATION_KEY = "settings.deals.stageStudio";

const STAGES_QUERY_KEY = ["settings/deals/stages"];

const StageStudioTab: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [isCreateStageModalOpen, setIsCreateStageModalOpen] = useState(false);
  const [localStages, setLocalStages] = useState<DealStage[]>([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingReorderData, setPendingReorderData] = useState<{
    movedStage: DealStage;
    newItems: DealStage[];
    onConfirm: () => void;
    modalType: confirmationModalType;
  } | null>(null);

  const { data: dealStagesData, isLoading, refetch } = useDealStages(true);
  const stages = dealStagesData?.stages || [];

  const createStageMutation = useCreateDealStage();
  const updateStagesOrderMutation = useUpdateDealStagesOrder();

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: STAGES_QUERY_KEY });
  }, [queryClient]);

  useEffect(() => {
    if (stages.length > 0) {
      setLocalStages([...stages]);
    }
  }, [stages]);

  const handleItemsChange = (updatedItems: DealStage[]) => {
    setLocalStages(
      updatedItems.map((item, index) => ({ ...item, stageOrder: index + 1 }))
    );
  };

  const handleCreateStage = (stageData: newStageFormData) => {
    const { name, milestone, positionInPipeline } = stageData;

    createStageMutation.mutate(
      { data: { name, milestone } },
      {
        onSuccess: (data) => {
          const newStageId = data?.id;
          if (!newStageId) return;

          const newOrder = calculateNewStageOrder(
            stages,
            newStageId,
            positionInPipeline
          );
          updateStagesOrderMutation.mutate({ data: newOrder });
          refetch();
          navigate(
            `${location.pathname}/stages/${newStageId}${location.search}`
          );

          toast.success(
            <ToastBody
              title={t(`${TRANSLATION_KEY}.createStage.successToast.title`, {
                stageName: name
              })}
              subtitle={t(
                `${TRANSLATION_KEY}.createStage.successToast.subtitle`
              )}
            />,
            { duration: 5000 }
          );
        }
      }
    );
  };

  return (
    <div className="flex flex-col w-full gap-12">
      <div className="flex gap-6 rounded-2xl border border-border-widget-card bg-surface-widget-card">
        <div className="p-6 max-w-[23rem]">
          <Text variant="lgBody">{t(`${TRANSLATION_KEY}.description`)}</Text>
        </div>
        <div className="w-full">
          <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain">
            <DealSettings />
          </div>
        </div>
      </div>
      <Section
        title={t(`${TRANSLATION_KEY}.stages.title`)}
        subTitle={t(`${TRANSLATION_KEY}.stages.subtitle`)}
        action={
          <AddActionButton
            label={t(`${TRANSLATION_KEY}.stages.create`)}
            onClick={() => setIsCreateStageModalOpen(true)}
            disabled={isLoading}
          />
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          localStages.length > 0 && (
            <DraggableStagesList<DealStage>
              items={localStages}
              onItemsChange={handleItemsChange}
              tPrefix={`${TRANSLATION_KEY}.stages`}
              queryKey={STAGES_QUERY_KEY}
              persistOrder={(orderedIds) =>
                updateStagesOrderMutation.mutateAsync({ data: orderedIds })
              }
              shouldConfirmReorder={({
                moved,
                sourceIndex,
                destinationIndex
              }) => {
                const stageHasDeals = (moved.dealsCount || 0) > 0;
                if (!stageHasDeals) return null;
                const modalType: confirmationModalType =
                  destinationIndex > sourceIndex
                    ? "moveStageForward"
                    : "moveStageBackward";
                return (commit) => {
                  setPendingReorderData({
                    movedStage: moved,
                    newItems: [],
                    onConfirm: commit,
                    modalType
                  });
                  setIsConfirmationModalOpen(true);
                };
              }}
              renderRow={({ item, index, isDragDisabled, isLast }) => (
                <DealStageRow
                  key={item.id}
                  item={item}
                  index={index}
                  tPrefix={`${TRANSLATION_KEY}.stages`}
                  isDragDisabled={isDragDisabled}
                  isLast={isLast}
                />
              )}
            />
          )
        )}
      </Section>
      {pendingReorderData && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          setIsOpen={setIsConfirmationModalOpen}
          title={t(`${TRANSLATION_KEY}.${pendingReorderData.modalType}.title`)}
          confirmationText={
            <div className="flex flex-col gap-4">
              <span>
                {t(
                  `${TRANSLATION_KEY}.${pendingReorderData.modalType}.message`
                )}
              </span>
              <span>
                {t(
                  `${TRANSLATION_KEY}.${pendingReorderData.modalType}.message2`
                )}
              </span>
            </div>
          }
          handleSubmit={() => {
            pendingReorderData.onConfirm();
            setPendingReorderData(null);
            setIsConfirmationModalOpen(false);
          }}
          handleCancel={() => {
            if (stages.length > 0) setLocalStages([...stages]);
            setPendingReorderData(null);
            setIsConfirmationModalOpen(false);
          }}
          isPrimaryButton={true}
          submitButtonText={t(
            `${TRANSLATION_KEY}.${pendingReorderData.modalType}.confirm`
          )}
        />
      )}
      <CreateNewStage
        isOpen={isCreateStageModalOpen}
        setIsOpen={setIsCreateStageModalOpen}
        onCreateStage={handleCreateStage}
      />
    </div>
  );
};

export default StageStudioTab;
